import React, { useState, useEffect } from 'react';
import api from '../../services/api';
import StatusBadge from '../../components/shared/StatusBadge';
import ProgressBar from '../../components/shared/ProgressBar';

const Firms = () => {
  const [firms, setFirms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingFirm, setEditingFirm] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    contactPerson: '',
    email: '',
    phone: '',
    address: ''
  });

  useEffect(() => {
    fetchFirms();
  }, []);

  const fetchFirms = async () => {
    try {
      const response = await api.get('/admin/firms');
      if (response.data.success) {
        setFirms(response.data.data.firms);
      }
    } catch (error) {
      console.error('Failed to fetch firms:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = () => {
    setEditingFirm(null);
    setFormData({
      name: '',
      contactPerson: '',
      email: '',
      phone: '',
      address: ''
    });
    setShowModal(true);
  };

  const handleEdit = (firm) => {
    setEditingFirm(firm);
    setFormData({
      name: firm.name,
      contactPerson: firm.contact_person || '',
      email: firm.email || '',
      phone: firm.phone || '',
      address: firm.address || ''
    });
    setShowModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingFirm) {
        await api.put(`/admin/firms/${editingFirm.id}`, {
          ...formData,
          isActive: editingFirm.is_active
        });
      } else {
        await api.post('/admin/firms', formData);
      }
      setShowModal(false);
      fetchFirms();
    } catch (error) {
      alert(error.response?.data?.message || 'Failed to save firm');
    }
  };

  const handleSendReminder = async (firmId) => {
    try {
      await api.post(`/admin/firms/${firmId}/remind`);
      alert('Reminder sent successfully');
    } catch (error) {
      alert(error.response?.data?.message || 'Failed to send reminder');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold text-dark">Broker Firms</h1>
        <button
          onClick={handleCreate}
          className="px-4 py-2 bg-primary text-white rounded-lg hover:bg-green-700 transition-colors"
        >
          Create Firm
        </button>
      </div>

      <div className="bg-white rounded-lg shadow overflow-hidden">
        <table className="min-w-full divide-y divide-border">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-grey uppercase tracking-wider">
                Firm Name
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-grey uppercase tracking-wider">
                Contact Person
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-grey uppercase tracking-wider">
                Email
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-grey uppercase tracking-wider">
                Status
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-grey uppercase tracking-wider">
                Submission Status
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-grey uppercase tracking-wider">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-border">
            {firms.map((firm) => (
              <tr key={firm.id} className="hover:bg-gray-50">
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="text-sm font-medium text-dark">{firm.name}</div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-grey">
                  {firm.contact_person || '-'}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-grey">
                  {firm.email || '-'}
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <span className={`px-2 py-1 rounded-full text-xs font-medium ${firm.is_active ? 'bg-green-100 text-green-600' : 'bg-red-100 text-red-600'}`}>
                    {firm.is_active ? 'Active' : 'Inactive'}
                  </span>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  {firm.submissions && firm.submissions.length > 0 ? (
                    <div>
                      <StatusBadge status={firm.submissions[0].status} />
                      <div className="mt-1 w-24">
                        <ProgressBar percentage={firm.submissions[0].completion_percentage || 0} />
                      </div>
                    </div>
                  ) : (
                    <span className="text-sm text-grey">Not started</span>
                  )}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm">
                  <button
                    onClick={() => handleEdit(firm)}
                    className="text-primary hover:text-green-700 mr-3"
                  >
                    Edit
                  </button>
                  <button
                    onClick={() => handleSendReminder(firm.id)}
                    className="text-orange hover:text-orange-600"
                  >
                    Remind
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-lg w-full mx-4">
            <div className="p-6 border-b border-border">
              <h2 className="text-xl font-semibold text-dark">
                {editingFirm ? 'Edit Firm' : 'Create Firm'}
              </h2>
            </div>
            <form onSubmit={handleSubmit} className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-grey mb-2">Firm Name *</label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="w-full px-4 py-2 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-grey mb-2">Contact Person</label>
                <input
                  type="text"
                  value={formData.contactPerson}
                  onChange={(e) => setFormData({ ...formData, contactPerson: e.target.value })}
                  className="w-full px-4 py-2 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-grey mb-2">Email</label>
                <input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  className="w-full px-4 py-2 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-grey mb-2">Phone</label>
                <input
                  type="text"
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  className="w-full px-4 py-2 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-grey mb-2">Address</label>
                <textarea
                  value={formData.address}
                  onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                  rows={3}
                  className="w-full px-4 py-2 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent resize-none"
                />
              </div>
            </form>
            <div className="p-6 border-t border-border flex justify-end space-x-3">
              <button
                onClick={() => setShowModal(false)}
                className="px-4 py-2 border border-border rounded-lg text-grey hover:border-primary hover:text-primary transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleSubmit}
                className="px-4 py-2 bg-primary text-white rounded-lg hover:bg-green-700 transition-colors"
              >
                {editingFirm ? 'Update' : 'Create'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Firms;
